import { LinkProps } from "@tanstack/react-router"
import { Section } from "design-system/molecules/Section"
import { useIntegrationsStatusQuery } from "modules/settings/hooks/useIntegrationsStatusQuery"
import { AmphoraIcon } from "modules/shared/icons/amphora"
import { BigBlueIcon } from "modules/shared/icons/bigblue"
import { FrontAppIcon } from "modules/shared/icons/frontapp"
import { GorgiasIcon } from "modules/shared/icons/gorgias"
import { KlaviyoIcon } from "modules/shared/icons/klaviyo"
import { LoopReturnsIcon } from "modules/shared/icons/loopReturns"
import { ReveniIcon } from "modules/shared/icons/reveni"
import { ReverIcon } from "modules/shared/icons/rever"
import { SalesForceIcon } from "modules/shared/icons/salesForce"
import { ShopifyIcon } from "modules/shared/icons/shopify"
import { ZendeskIcon } from "modules/shared/icons/zendesk"
import { useTranslation } from "react-i18next"
import { SettingsCard, SettingsSection } from "../SettingsCard"

type Integrations = SettingsSection & {
  type?: "Channel" | "CRM" | "Integration"
  beta?: boolean
  isActive?: boolean
  isEnabled?: boolean
  disabledReason?: string
  displayOnly?: boolean
}

type IntegrationGroup = {
  title: string
  description?: string
  options: Integrations[]
}

export const CompanyIntegrations = () => {
  const { t } = useTranslation()

  const { data, isPending } = useIntegrationsStatusQuery()

  const amphoraDisabledReason = data?.bigblue
    ? t("settings.integrations.disabledByBigBlue")
    : t("settings.integrations.disabledRequiresShopify")
  const bigBlueDisabledReason = data?.amphora
    ? t("settings.integrations.disabledByAmphora")
    : t("settings.integrations.disabledRequiresShopify")
  const returnsIntegrationDisabledReason = t("settings.integrations.disabledRequiresShopify")

  const integrations: IntegrationGroup[] = [
    {
      title: t("settings.integrations.groups.ecommerce.title"),
      options: [
        {
          id: "shopify",
          title: t("settings.integrations.shopify.title"),
          description: t("settings.integrations.shopify.description"),
          icon: ShopifyIcon,
          type: "Integration",
          navigate: {
            to: "/settings/integrations/shopify",
          },
          isActive: data?.shopify,
        },
        {
          id: "amphora",
          icon: AmphoraIcon,
          title: t("settings.integrations.amphora.title"),
          description: t("settings.integrations.amphora.description"),
          type: "Integration",
          navigate: {
            to: "/settings/integrations/amphora",
          },
          isActive: data?.amphora,
          isEnabled: data?.enabled.amphora,
          disabledReason: amphoraDisabledReason,
        },
        {
          id: "bigblue",
          title: t("settings.integrations.bigblue.title"),
          description: t("settings.integrations.bigblue.description"),
          icon: BigBlueIcon,
          type: "Integration",
          navigate: {
            to: "/settings/integrations/bigblue",
          },
          isActive: data?.bigblue,
          isEnabled: data?.enabled.bigblue,
          disabledReason: bigBlueDisabledReason,
        },
        {
          id: "reveni",
          icon: ReveniIcon,
          title: t("settings.integrations.reveni.title"),
          description: t("settings.integrations.reveni.description"),
          type: "Integration",
          navigate: {
            to: "/settings/integrations/reveni",
          },
          isActive: data?.reveni,
          isEnabled: data?.enabled.reveni,
          disabledReason: returnsIntegrationDisabledReason,
        },
        {
          id: "loopReturns",
          icon: LoopReturnsIcon,
          title: t("settings.integrations.loopReturns.title"),
          description: t("settings.integrations.loopReturns.description"),
          type: "Integration",
          navigate: { to: "/settings/integrations/loopReturns" },
          isActive: data?.loopReturns,
          isEnabled: data?.enabled.loopReturns,
          disabledReason: returnsIntegrationDisabledReason,
        },
        {
          id: "rever",
          icon: ReverIcon,
          title: t("settings.integrations.rever.title"),
          description: t("settings.integrations.rever.description"),
          type: "Integration",
          navigate: { to: "/settings/integrations/rever" },
          isActive: data?.rever,
          isEnabled: data?.enabled.rever,
          disabledReason: returnsIntegrationDisabledReason,
        },
      ],
    },
    {
      title: t("settings.integrations.groups.support.title"),
      options: [
        {
          id: "zendesk",
          title: t("settings.integrations.zendesk.title"),
          description: t("settings.integrations.zendesk.description"),
          icon: ZendeskIcon,
          type: "Integration",
          navigate: {
            to: "/settings/integrations/zendesk",
          },
          isActive: data?.zendesk,
        },
        {
          id: "gorgias",
          title: t("settings.integrations.gorgias.title"),
          description: t("settings.integrations.gorgias.description"),
          icon: GorgiasIcon,
          type: "Integration",
          navigate: {
            to: "/settings/integrations/gorgias",
          },
          isActive: data?.gorgias,
        },
        {
          id: "frontapp",
          title: t("settings.integrations.frontapp.title"),
          description: t("settings.integrations.frontapp.description"),
          icon: FrontAppIcon,
          type: "Integration",
          navigate: {
            to: "/settings/integrations/frontapp",
          },
          isActive: data?.frontapp,
        },
      ],
    },
    {
      title: t("settings.integrations.groups.crms.title"),
      options: [
        {
          id: "salesForce",
          title: t("settings.integrations.salesForce.title"),
          description: t("settings.integrations.salesForce.description"),
          icon: SalesForceIcon,
          type: "CRM",
          navigate: {
            to: "/settings/integrations/salesForce",
          },
          isActive: data?.salesForce,
        },
        {
          id: "klaviyo",
          title: t("settings.integrations.klaviyo.title"),
          description: t("settings.integrations.klaviyo.description"),
          icon: KlaviyoIcon,
          type: "CRM",
          navigate: {
            to: "/settings/integrations/klaviyo",
          },
          isActive: data?.klaviyo,
        },
      ],
    },
  ]

  return (
    <div className="flex flex-col gap-8">
      {integrations.map((integration) => {
        return (
          <div key={integration.title} className="flex flex-col gap-4 group">
            <Section title={integration.title} description={integration.description} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {integration.options.map((option) => {
                const hasNavigate = "navigate" in option
                const hasDisplayOnly = "displayOnly" in option

                let sectionExtra:
                  { navigate: LinkProps } | { displayOnly: true } | { comingSoon: true }

                if (hasNavigate) {
                  sectionExtra = { navigate: option.navigate }
                } else if (hasDisplayOnly) {
                  sectionExtra = { displayOnly: true }
                } else {
                  sectionExtra = { comingSoon: true }
                }

                const section: SettingsSection = {
                  id: option.id,
                  title: option.title,
                  description: option.description,
                  icon: "icon" in option ? option.icon : undefined,
                  isActive: !isPending && option.isActive,
                  isDisabled: option.isEnabled === false,
                  disabledReason: option.isEnabled === false ? option.disabledReason : undefined,
                  ...sectionExtra,
                }

                return <SettingsCard key={option.id} section={section} />
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
